import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "explainer-text" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 2 }
const _hoisted_5 = { key: 3 }
const _hoisted_6 = { class: "selected-timezone-details d-flex mb-4" }
const _hoisted_7 = { class: "d-flex flex-row flex-wrap" }
const _hoisted_8 = { class: "text-caption" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { class: "d-flex ga-2" }
const _hoisted_11 = { style: {"height":"calc(100% - 40px)"} }
const _hoisted_12 = {
  id: "below-graph-stuff",
  class: "mt-2 explainer-text"
}
const _hoisted_13 = {
  key: 0,
  id: "aggregation-warning"
}

import { ref, computed, watch, nextTick } from 'vue';
import { storeToRefs } from "pinia";
import { v4 } from 'uuid';
import { TimeSeriesFolder, sortfoldBinContent } from '../esri/services/aggregation';
import FoldedPlotlyGraph from './FoldedPlotlyGraph.vue';
import type { Prettify, UserDataset, PlotltGraphDataSet, UnifiedRegion } from '../types';
import type { TimeRangeSelectionType } from '@/types/datetime';
import type { AggregationMethod, TimeSeriesData, FoldedTimeSeriesData , FoldType, FoldBinContent} from '../esri/services/aggregation';
import tz_lookup from '@photostructure/tz-lookup';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { useTempoStore } from '@/stores/app';
import {
  TimeBinOptions,
  FoldingPeriodOptions,
  isValidCombination,
  getFirstValidTimeBin
} from '@/utils/foldingValidation';

interface DataFoldingProps {
  selection: UserDataset | null;
}

const MS_IN_HOUR = 3600000;

export default /*@__PURE__*/_defineComponent({
  __name: 'DataFoldingAndBinning',
  props: /*@__PURE__*/_mergeModels({
    selection: {}
  }, {
    "modelValue": { type: Boolean, ...{ type: Boolean, required: true } },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["save"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {

// eslint-disable @typescript-eslint/no-unused-vars */
const store = useTempoStore();
const {
  debugMode,
} = storeToRefs(store);

const props = __props;

const emit = __emit;

// Dialog state
const dialogOpen = _useModel<boolean>(__props, 'modelValue');

const theColor = computed(() => {
  return props.selection?.customColor ?? (props.selection?.region.color ?? 'blue');
});


const MS_IN_DAY = MS_IN_HOUR * 24;
const MS_IN_WEEK = MS_IN_DAY * 7;
const MS_IN_MONTH = MS_IN_DAY * 28; // to avoid invalidating February
const MS_IN_YEAR = MS_IN_DAY * 365.25;

const timeBinDurations: Record<TimeBinOptions, number> = {
  'hour': MS_IN_HOUR,
  'day': MS_IN_DAY,
  'week': MS_IN_WEEK,
  'month': MS_IN_MONTH, 
  'none': 0,
};

const foldingPeriodDurations: Record<FoldingPeriodOptions, number> = {
  'day': MS_IN_DAY,
  'week': MS_IN_WEEK,
  'month' : MS_IN_MONTH,
  'year': MS_IN_YEAR,
  'weekdayWeekend': MS_IN_WEEK, // weekend is part of week
  'none': 0,
};

const dataDuration = computed(() => {
  if (!props.selection?.samples) return 0;
  const timestamps = Object.values(props.selection.samples).map(s => s.date.getTime());
  if (timestamps.length === 0) return 0;
  // single loop to find min and max on unsorted array
  // (but it may already be sorted, but we only need to do this once so it's fine)
  const minMax = timestamps.reduce((acc, t) => {
    return {
      min: Math.min(acc.min, t),
      max: Math.max(acc.max, t)
    };
  }, { min: Infinity, max: -Infinity });
  return minMax.max - minMax.min;
});

function validTimeBinForData(timeBin: TimeBinOptions): boolean {
  if (dataDuration.value === 0) return false;

  return dataDuration.value >= timeBinDurations[timeBin];
}

function validFoldingForData(foldType: FoldingPeriodOptions): boolean {
  if (dataDuration.value === 0) return false;
  if (foldType === 'weekdayWeekend') {
    return (props.selection?.samples && Object.values(props.selection?.samples).map(s => s.date.getDay()).some(d => d === 0 || d === 6)) as boolean; // has at least one weekend day
  }
  return dataDuration.value >= foldingPeriodDurations[foldType];
}

const aggregationWarning = ref('');


// Time bin and folding period options
const timeBinOptions: {title: string, value: TimeBinOptions}[] = [
  { title: 'None', value: 'none' },
  { title: 'Hour', value: 'hour' },
  { title: 'Day', value: 'day' },
  { title: 'Week', value: 'week' },
  { title: 'Month', value: 'month' }
];

const allFoldingPeriodOptions: {title: string, value: FoldingPeriodOptions}[] = [
  { title: 'None', value: 'none' },
  { title: 'Day', value: 'day' },
  { title: 'Week', value: 'week' },
  { title: 'Month', value: 'month' },
  { title: 'Year', value: 'year' },
  { title: 'Weekend/Weekday', value: 'weekdayWeekend' },
];



// Computed property to filter valid folding periods based on selected time bin
const foldingPeriodOptions = computed(() => {
  return allFoldingPeriodOptions;
});


const methodOptions = [
  { title: 'Mean', value: 'mean' },
  { title: 'Median', value: 'median' },
  { title: 'Min', value: 'min' },
  { title: 'Max', value: 'max' }
];

const timezoneOptions = [
  { title: 'US/Eastern', value: 'US/Eastern' },
  { title: 'US/Central', value: 'US/Central' },
  { title: 'US/Mountain', value: 'US/Mountain' },
  { title: 'US/Arizona', value: 'US/Arizona' },
  { title: 'US/Pacific', value: 'US/Pacific' },
  { title: 'UTC', value: 'UTC' }
];

// Reactive state
const selectedTimeBin = ref<TimeBinOptions>('none');
const selectedFoldingPeriod = ref<FoldingPeriodOptions>('none');
const selectedMethod = ref<AggregationMethod>('mean');
const selectedTimezone = ref('US/Eastern');
const showErrors = ref(true);
const useSEM = ref(true);
const useErrorBars = ref(false);

watch(selectedFoldingPeriod, (newPeriod) => {
  if (!isValidCombination(selectedTimeBin.value, newPeriod)) {
    // Set to first valid option
    const validTimeBin = getFirstValidTimeBin(newPeriod);
    if (validTimeBin && validTimeBinForData(validTimeBin)) {
      selectedTimeBin.value = validTimeBin;
    } else {
      selectedTimeBin.value = 'hour'; // default fallback
    }
  }
});

// Computed FoldType based on time bin and folding period selections
const selectedFoldType = computed<FoldType>(() => {
  // Map the combination to the appropriate FoldType
  const timeBin = selectedTimeBin.value;
  const period = selectedFoldingPeriod.value;
    
  const foldType = `${timeBin}Of${period.charAt(0).toUpperCase()}${period.slice(1)}` as FoldType;
  console.log('Computed FoldType:', foldType, 'from', timeBin, 'and', period);
  return foldType;
});




// Check if we're using a None-period fold type (no actual folding)
const isNonePeriod = computed(() => {
  return selectedFoldingPeriod.value === 'none';
});

// Check if we're using any hour-based fold type (which is always centered)
const isHourBinned = computed(() => {
  return selectedTimeBin.value === 'hour';
});

const isFoldWithNoBin = computed(() => {
  return selectedTimeBin.value === 'none' && selectedFoldingPeriod.value !== 'none';
});

const includeBinPhase = ref(true);
// const alignToBinCenter = ref(true);
const alignToBinCenter = computed(() => {
  if (isNonePeriod.value) return true;
  if (selectedFoldingPeriod.value === 'weekdayWeekend') return false;
  if (includeBinPhase.value === false) return false;
  if (isHourBinned.value) return true;
  if (selectedTimeBin.value === 'week' && selectedFoldingPeriod.value === 'year') return false;
  return true;
});

const alignDataToBinCenters = computed({
  get() {
    return !includeBinPhase.value;
  },
  set(value: boolean) {
    includeBinPhase.value = !value;
  }
});


const disableBinCenterCheckbox = computed(() => {
  return !isNonePeriod.value && includeBinPhase.value && !isHourBinned.value;
});

const disableIncludePhaseCheckbox = computed(() => {
  return !isNonePeriod.value;
});

// Watch to ensure selected folding period is valid when time bin changes
watch(selectedTimeBin, () => {
  const validPeriods = foldingPeriodOptions.value.map(opt => opt.value);
  if (!validPeriods.includes(selectedFoldingPeriod.value)) {
    // Set to first valid option
    if (validPeriods.length > 0) {
      selectedFoldingPeriod.value = validPeriods[0];
    }
  }
});

// Watch for None-period types and reset incompatible options
watch(isNonePeriod, (isNone) => {
  if (isNone) {
    includeBinPhase.value = true;
    // alignToBinCenter.value = true;
  }
});

const regionCenter = computed(() => {
  const region = props.selection?.region as UnifiedRegion;
  
  if (region && region.geometryType === 'point') {
    return { lat: region.geometryInfo.y, lon: region.geometryInfo.x };
  }
  
  if (region && region.geometryType === 'rectangle') {
    const { xmin, ymin, xmax, ymax } = region.geometryInfo;
    return { lat: (ymin + ymax) / 2, lon: (xmin + xmax) / 2 };
  }
  
  return { lat: 0, lon: 0 };
});



const useTzCenter = ref(true);
const tzCenter = tz_lookup(regionCenter.value.lat, regionCenter.value.lon);

if (regionCenter.value.lat !== 0 || regionCenter.value.lon !== 0) {
  const tz = tz_lookup(regionCenter.value.lat, regionCenter.value.lon);
  if (tz && useTzCenter.value) {
    selectedTimezone.value = tz;
  }
}

watch(useTzCenter, (newVal) => {
  if (newVal && regionCenter.value.lat !== 0 && regionCenter.value.lon !== 0) {
    const tz = tz_lookup(regionCenter.value.lat, regionCenter.value.lon);
    if (tz) {
      selectedTimezone.value = tz;
    }
  }
});

// Computed properties
const originalDataPointCount = computed(() => {
  if (!props.selection?.samples) return 0;
  return Object.keys(props.selection.samples).length;
});

const foldedDataPointCount = computed(() => {
  if (!foldedData.value) return 0;
  return Object.keys(foldedData.value.values).length;
});

const canSave = computed(() => {
  return !!(props.selection && foldedData.value && foldedDataPointCount.value > 0);
});

const foldedDatasetName = computed(() => {
  if (!props.selection?.name) return 'Folded Data';
  return `Aggregated ${props.selection.name ?? props.selection.region.name}`;// (${selectedTimeBin.value} of ${selectedFoldingPeriod.value}, ${selectedMethod.value})`;
});

// Aggregated data
const foldedData = ref<FoldedTimeSeriesData | null>(null);
const foldedSelection = ref<null>(null);
// Graph data for display - now a ref that gets manually updated
const graphData = ref<PlotltGraphDataSet[]>([]);

watch(foldedData, (newvValue) => {
  // if there is only one bin present the user with an aggregation warning
  if (newvValue && newvValue.bins && Object.keys(newvValue.bins).length <= 1) {
    aggregationWarning.value = ("Warning: This combination of time bin and folding period results " +
      "in only one bin. Please select a smaller time bin " + 
      "or a different folding period if available. In most cases, it is recommend to select 'None' for the folding period.");
  } else {
    aggregationWarning.value = '';
  }
});


// eslint-disable-next-line @typescript-eslint/no-unused-vars
function timeseriesToDataSet(timeseries: TimeSeriesData): Omit<PlotltGraphDataSet, 'name'> {
  const x: PlotltGraphDataSet['x'] = [];
  const y: PlotltGraphDataSet['y'] = [];
  const lower: PlotltGraphDataSet['lower'] = [];
  const upper: PlotltGraphDataSet['upper'] = [];

  // tsa, tsb are the timestamps as strings
  const sortedEntries = Object.entries(timeseries.values).sort(([tsa, _a], [tsb, _b]) => parseInt(tsa) - parseInt(tsb));
  // get the first timestamp
  sortedEntries.forEach(([timestamp, aggValue]) => {
    y.push(aggValue.value);
    x.push(new Date(parseInt(timestamp)));
    const error = timeseries.errors[timestamp];
    lower.push(error?.lower ?? null);
    upper.push(error?.upper ?? null);
  });

  return { x, y, lower, upper };
}

function foldedTimesSeriesToDataSet(foldedTimeSeries: FoldedTimeSeriesData): Omit<PlotltGraphDataSet, 'name'> {
  const x: (number | Date | null)[] = [];
  const y: (number | null)[] = [];
  const lower: (number | null)[] = [];
  const upper: (number | null)[] = [];

  // Check if this is a None-period fold type
  const isNonePeriod = ['noneOfNone','hourOfNone', 'dayOfNone', 'weekOfNone', 'monthOfNone'].includes(foldedTimeSeries.foldType);

  // tsa, tsb are the timestamps as strings
  const sortedEntries = Object.entries(foldedTimeSeries.bins).sort(([binIndexa, _a], [binIndexb, _b]) => parseInt(binIndexa) - parseInt(binIndexb));

  sortedEntries.forEach(([binIndex, _binContent]) => {
    const idx = parseInt(binIndex);
    const aggValue = foldedTimeSeries.values[idx];
    
    // Use date if available (for None-period types), otherwise use bin index
    if (isNonePeriod && aggValue.date) {
      // For None-period types with dates, center bins by adding half the bin width
      if (alignToBinCenter.value) {
        const date = new Date(aggValue.date);
        switch (foldedTimeSeries.foldType) {
        case 'hourOfNone':
          date.setMinutes(30); // Center of hour
          break;
        case 'dayOfNone':
          date.setHours(date.getHours() + 12); // local Noon
          break;
        case 'weekOfNone':
          date.setDate(date.getDate() + 3); // Middle of week (approx)
          date.setHours(date.getHours() + 12);
          break;
        case 'monthOfNone':
          date.setDate(15); // Middle of month (approx)
          break;
        }
        x.push(date);
      } else {
        x.push(aggValue.date);
      }
    } else {
      if (foldedTimeSeries.foldType !== 'hourOfDay') {
        x.push(idx + (alignToBinCenter.value ? 0.5 : 0));
      } else {
        x.push(idx);
      }
    }
    
    y.push(aggValue.value);
    const error = foldedTimeSeries.errors[idx];
    lower.push(error?.lower ?? null);
    upper.push(error?.upper ?? null);
  });

  return { x, y, lower, upper, errorType: useErrorBars.value ? 'bar' : 'band' };
}
  
function foldedTimeSeriesRawToDataSet(foldedTimeSeries: FoldedTimeSeriesData): Omit<PlotltGraphDataSet, 'name'> {
  const x: (number | Date | null)[] = [];
  const y: (number | null)[] = [];
  const lower: (number | null)[] = [];
  const upper: (number | null)[] = [];
  const customdata: Date[] = [];

  // Check if this is a None-period fold type
  const isNonePeriod = ['hourOfNone', 'dayOfNone', 'weekOfNone', 'monthOfNone'].includes(foldedTimeSeries.foldType);

  // tsa, tsb are the timestamps as strings
  const sortedEntries = Object.entries(foldedTimeSeries.bins).sort(([binIndexa, _a], [binIndexb, _b]) => parseInt(binIndexa) - parseInt(binIndexb));

  sortedEntries.forEach(([_binIndex, binContent]) => {
    const sortedBinContent = sortfoldBinContent(binContent);
    const bins = sortedBinContent as Prettify<FoldBinContent>;
    
    bins.rawValues.forEach((rv, index) => {
      if (isNonePeriod) {
        // For None-period types, use the actual timestamp
        x.push(new Date(bins.timestamps[index]));
      } else {
        // For regular fold types, use bin index with optional phase
        x.push(bins.bin + (includeBinPhase.value ? bins.binPhase[index] : 0));
      }
      customdata.push(toZonedTime(new Date(bins.timestamps[index]), selectedTimezone.value));
      y.push(rv);
    });
    bins.lowers.forEach(low => {
      lower.push(low ?? null);
    });
    bins.uppers.forEach(up => {
      upper.push(up ?? null);
    });
  });
  // the ascii +- symbol is this characher: ±
  const hovertemplate = '%{customdata|%Y-%m-%d %H:%M}<br>%{y:0.2e}±%{error_y.array:0.2e}<extra></extra>';
  
  return { x, y, lower, upper, errorType: 'bar', datasetOptions: { customdata, hovertemplate } };
}

// Function to update graph data
function updateGraphData() {
  console.log("Updating graph data");
  if (!props.selection) {
    graphData.value = [];
    return;
  }
  
  // const data = [timeseriesToDataSet(selectionToTimeseries(props.selection))]; // Original data
  const data: PlotltGraphDataSet[] = [];
  
  if (foldedData.value) {
    const t = foldedTimeSeriesRawToDataSet(foldedData.value); // Raw folded data
    (t as PlotltGraphDataSet).name = props.selection.name || 'Original Data';
    data.push(t as PlotltGraphDataSet); // Raw folded data
    if (!isFoldWithNoBin.value) {
      const f = foldedTimesSeriesToDataSet(foldedData.value); // Summary folded data
      (f as PlotltGraphDataSet).name = foldedDatasetName.value;
      data.push(f as PlotltGraphDataSet); // Summary folded data
    }
  } else {
    // No folded data, just show original
    const original = timeseriesToDataSet(selectionToTimeseries(props.selection));
    (original as PlotltGraphDataSet).name = props.selection.name || 'Original Data';
    data.push(original as PlotltGraphDataSet);
  }
  console.log("Prepared graph data:", data);
  graphData.value = data;
  console.log("Graph data updated with", data.length, "datasets");
}

// Create a time range for the folded data
function createFoldedTimeRange() {
  if (!props.selection) {
    throw new Error('No selection available');
  }
  
  const originalRange = props.selection.timeRange.range;
  const ranges = Array.isArray(originalRange) ? originalRange : [originalRange];
  
  return {
    id: v4(),
    name: `Folded (${selectedTimeBin.value} of ${selectedFoldingPeriod.value})`,
    description: `Folded data (${selectedTimeBin.value} of ${selectedFoldingPeriod.value}) ${selectedMethod.value}`,
    range: ranges,
    type: 'folded' as TimeRangeSelectionType,
  };
}


// Only recalculate when data-affecting parameters change
watch([selectedTimeBin, selectedFoldingPeriod, selectedMethod, selectedTimezone, useSEM], 
  updateAggregatedData, { immediate: true });

// Handle display-only changes separately
watch([useErrorBars, alignToBinCenter, includeBinPhase], updateGraphData);

function selectionToTimeseries(selection: UserDataset): TimeSeriesData {
  return {
    values: selection.samples || {},
    errors: selection.errors || {},
    locations: selection.locations || [],
    geometryType: selection.region.geometryType || 'rectangle'
  };
}

// Update folded data when parameters change
function updateAggregatedData() {
  console.log("Updating folded data with time bin:", selectedTimeBin.value, "folding period:", selectedFoldingPeriod.value, "fold type:", selectedFoldType.value, "method:", selectedMethod.value, "timezone:", selectedTimezone.value);
  if (!props.selection?.samples) {
    foldedData.value = null;
    return;
  }
  
  try {
    
    // Convert the selection data to TimeSeriesData format
    const timeSeriesData = selectionToTimeseries(props.selection);
    
    let foldType = selectedFoldType.value;
    if (isFoldWithNoBin.value) {
      foldType = foldType.replace('noneOf', 'hourOf') as FoldType;
    }
    
    const grouper = new TimeSeriesFolder(
      foldType,  // Use the computed fold type
      selectedTimezone.value, 
      selectedMethod.value, 
      useSEM.value ? 'sem' : 'std', true);
    foldedData.value = grouper.foldData(timeSeriesData);
    
  } catch (error) {
    console.error('Error aggregating data:', error);
    foldedData.value = null;
    foldedSelection.value = null;
  }
  
  // Update graph data after folding
  updateGraphData();
  console.log("graphData after update:", graphData.value);
}

function handlePointClick(value: {x: Plotly.Datum, y: number, customdata: unknown}) {
  console.log("Point clicked:", value);
  console.log("Custom data Date:", value.customdata ? value.customdata as Date: value.customdata);
  // the from timezoned time is what we want to to send work with if we go back to esri stuff
  console.log("fromZonedTime", value.customdata ? fromZonedTime(value.customdata as Date, selectedTimezone.value) : value.customdata);
  return;
}

// Save the folding
function saveFolding() {
  
  if (!canSave.value || !props.selection || !foldedData.value) return;
  
  // const rawDataset = foldedTimeSeriesRawToDataSet(foldedData.value);
  // (rawDataset as PlotltGraphDataSet).name = props.selection.name || 'Original Data';
  // const summaryDataset = foldedTimesSeriesToDataSet(foldedData.value);
  // (summaryDataset as PlotltGraphDataSet).name = foldedDatasetName.value;

  const foldedSelection: UserDataset = {
    id: v4(),
    region: { ...props.selection.region, name: props.selection.region.name } as typeof props.selection.region,
    timeRange: createFoldedTimeRange(),
    molecule: props.selection.molecule,
    customColor: theColor.value,
    loading: false, // folded data is immediately available
    // samples/errors intentionally omitted for folded since bins are synthetic; rely on plotlyDatasets
    locations: foldedData.value.locations,
    name: foldedDatasetName.value,
    folded: {
      timeBin: selectedTimeBin.value,
      foldingPeriod: selectedFoldingPeriod.value,
      foldType: selectedFoldType.value,
      method: selectedMethod.value,
      timezone: selectedTimezone.value,
      useSEM: useSEM.value,
      includeBinPhase: includeBinPhase.value,
      alignToBinCenter: alignToBinCenter.value,
      useErrorBars: useErrorBars.value,
      raw: foldedData.value
    },
    plotlyDatasets: [
      {
        ...graphData.value[0],
        datasetOptions: {
          ...graphData.value[0].datasetOptions,
          mode: 'markers',
          // hovertemplate: '%{customdata|%Y-%m-%d %H:%M}<br>%{y:0.2e}±%{error_y.array:0.2e}<extra></extra>'
        }
      } as PlotltGraphDataSet,
      {
        ...graphData.value[1],
        datasetOptions: {
          mode: 'markers'
        }
      } as PlotltGraphDataSet
    ].slice(0, isFoldWithNoBin.value ? 1 : 2) // only include summary if not fold-with-no-bin
  };
  console.log(foldedSelection);
  emit('save', foldedSelection);

  closeDialog();
}

// Close dialog
function closeDialog() {
  dialogOpen.value = false;
  // Reset state
  nextTick(() => {
    foldedData.value = null;
  });
}

// Watch for selection changes
watch(() => props.selection, () => {
  if (props.selection) {
    updateAggregatedData();
  }
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  const _component_v_toolbar_title = _resolveComponent("v-toolbar-title")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_toolbar = _resolveComponent("v-toolbar")!
  const _component_info_button = _resolveComponent("info-button")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_chip_group = _resolveComponent("v-chip-group")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_card_subtitle = _resolveComponent("v-card-subtitle")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!

  return (_openBlock(), _createBlock(_component_v_card, { id: "data-aggregation-card" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_toolbar, {
        density: "compact",
        color: "var(--info-background)"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_toolbar_title, null, {
            default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
              _createTextVNode("Data Aggregation", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_v_spacer),
          _createVNode(_component_v_btn, {
            icon: "mdi-close",
            onClick: closeDialog
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_card_text, { class: "pa-4" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, {
                cols: "12",
                md: "4"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card, {
                    variant: "outlined",
                    class: "pa-3"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("h3", null, [
                        _cache[12] || (_cache[12] = _createTextVNode(" Folding Period ", -1)),
                        _createVNode(_component_info_button, null, {
                          default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                            _createElementVNode("div", { class: "explainer-text" }, [
                              _createElementVNode("div", null, " Greyed out options are options which are either not able to applied to the current dataset (due to insufficient data), or which are incompatible with other selected options. "),
                              _createElementVNode("hr"),
                              _createElementVNode("strong", { class: "text-red" }, "FIX"),
                              _createTextVNode(" We \"fold\" data, by stacking data re-aligning data based on a periodic cycle. For example, if we fold by \"Week\", then Select the period over which to fold the data. Selecting \"None\" will simply bin the data without folding. ")
                            ], -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _createVNode(_component_v_chip_group, {
                        modelValue: selectedFoldingPeriod.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedFoldingPeriod).value = $event)),
                        column: "",
                        class: "mb-3"
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(foldingPeriodOptions.value, (option) => {
                            return (_openBlock(), _createBlock(_component_v_chip, {
                              key: option.value,
                              value: option.value,
                              color: "#092088",
                              variant: option.value === selectedFoldingPeriod.value ? 'flat' : 'outlined',
                              outline: "",
                              density: "compact",
                              disabled: !validFoldingForData(option.value as FoldingPeriodOptions)
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(option.title), 1)
                              ]),
                              _: 2
                            }, 1032, ["value", "variant", "disabled"]))
                          }), 128))
                        ]),
                        _: 1
                      }, 8, ["modelValue"]),
                      _createElementVNode("h3", null, [
                        _cache[20] || (_cache[20] = _createTextVNode("Time Bin ", -1)),
                        _createVNode(_component_info_button, null, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_1, [
                              _cache[17] || (_cache[17] = _createElementVNode("strong", { class: "text-red" }, "FIX", -1)),
                              _cache[18] || (_cache[18] = _createTextVNode(" Select the time bin for which we will aggregate the data. When aggregating data, we take all of the data in a bin (say the 1pm bin) and computed a single (aggregated) value for it, such as a mean or a max value. ", -1)),
                              _cache[19] || (_cache[19] = _createElementVNode("hr", null, null, -1)),
                              _createElementVNode("div", null, [
                                (selectedTimeBin.value === 'hour')
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[13] || (_cache[13] = [
                                      _createElementVNode("dt", null, "Hour", -1),
                                      _createElementVNode("dd", null, " Rounds data to the nearest hour. For example: Date from 12:30pm to 1:29pm will go into the 1pm bin. The binned point, shown in black is placed on the hour (e.g. at 1:00pm). ", -1)
                                    ]))]))
                                  : (selectedTimeBin.value === 'day')
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[14] || (_cache[14] = [
                                        _createElementVNode("dt", null, "Day", -1),
                                        _createElementVNode("dd", null, "Bins all data within a date. For example, all data points occuring on Dec 5 2025, or on Aug 28 2024, etc. The binned point, shown in black is placed at local noon (12:00pm) of that day. ", -1)
                                      ]))]))
                                    : (selectedTimeBin.value === 'week')
                                      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[15] || (_cache[15] = [
                                          _createElementVNode("dt", null, "Week", -1),
                                          _createElementVNode("dd", null, " Rounds data to the nearest week. Weeks start on Sunday. For example: Date from Sunday 12:00am to Saturday 11:59pm will go into the week bin. The binned point, shown in black is placed at local noon (12:00pm) on Wednesday of that week. ", -1)
                                        ]))]))
                                      : (selectedTimeBin.value === 'month')
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[16] || (_cache[16] = [
                                            _createElementVNode("dt", null, "Month", -1),
                                            _createElementVNode("dd", null, " Rounds data to the nearest month. For example: Date from the 1st of the month 12:00am to the last day of the month 11:59pm will go into the month bin. The binned point, shown in black is placed at local noon (12:00pm) on the 15th of that month. ", -1)
                                          ]))]))
                                        : _createCommentVNode("", true)
                              ])
                            ])
                          ]),
                          _: 1
                        })
                      ]),
                      _createVNode(_component_v_chip_group, {
                        modelValue: selectedTimeBin.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedTimeBin).value = $event)),
                        column: "",
                        class: "mb-3"
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(timeBinOptions, (option) => {
                            return _createVNode(_component_v_chip, {
                              key: option.value,
                              value: option.value,
                              color: "#092088",
                              variant: option.value === selectedTimeBin.value ? 'flat' : 'outlined',
                              density: "compact",
                              disabled: !validTimeBinForData(option.value as TimeBinOptions) || !_unref(isValidCombination)(option.value as TimeBinOptions, selectedFoldingPeriod.value)
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(option.title), 1)
                              ]),
                              _: 2
                            }, 1032, ["value", "variant", "disabled"])
                          }), 64))
                        ]),
                        _: 1
                      }, 8, ["modelValue"]),
                      _createElementVNode("div", _hoisted_6, [
                        _createVNode(_component_v_checkbox, {
                          modelValue: useTzCenter.value,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((useTzCenter).value = $event)),
                          label: `Use timezone of region center: ${_unref(tzCenter)}`,
                          density: "compact",
                          "hide-details": "",
                          class: "mb-1"
                        }, null, 8, ["modelValue", "label"])
                      ]),
                      (!useTzCenter.value)
                        ? (_openBlock(), _createBlock(_component_v_select, {
                            key: 0,
                            modelValue: selectedTimezone.value,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedTimezone).value = $event)),
                            items: timezoneOptions,
                            label: "Timezone",
                            density: "compact",
                            variant: "outlined",
                            "hide-details": "",
                            class: "mb-3"
                          }, null, 8, ["modelValue"]))
                        : _createCommentVNode("", true),
                      _createVNode(_component_v_select, {
                        modelValue: selectedMethod.value,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedMethod).value = $event)),
                        items: methodOptions,
                        label: "Aggregation Method",
                        density: "compact",
                        variant: "outlined",
                        "hide-details": "",
                        class: "mb-3"
                      }, null, 8, ["modelValue"]),
                      _createElementVNode("div", _hoisted_7, [
                        _createVNode(_component_v_checkbox, {
                          modelValue: showErrors.value,
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showErrors).value = $event)),
                          label: "Show Errors",
                          density: "compact",
                          disabled: selectedMethod.value == 'min' || selectedMethod.value == 'max',
                          "hide-details": "",
                          class: "mb-3"
                        }, null, 8, ["modelValue", "disabled"]),
                        _createVNode(_component_v_checkbox, {
                          modelValue: useErrorBars.value,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((useErrorBars).value = $event)),
                          label: "Use Error Bars",
                          density: "compact",
                          "hide-details": "",
                          class: "mb-3"
                        }, null, 8, ["modelValue"]),
                        _createVNode(_component_v_checkbox, {
                          modelValue: useSEM.value,
                          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((useSEM).value = $event)),
                          label: "Use Standard Error of the Mean",
                          density: "compact",
                          "hide-details": "",
                          class: "mb-3"
                        }, null, 8, ["modelValue"]),
                        _createVNode(_component_v_checkbox, {
                          modelValue: alignDataToBinCenters.value,
                          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((alignDataToBinCenters).value = $event)),
                          label: "Align Data to bins",
                          density: "compact",
                          disabled: !disableIncludePhaseCheckbox.value,
                          "hide-details": "",
                          class: "mb-3"
                        }, null, 8, ["modelValue", "disabled"]),
                        false
                          ? (_openBlock(), _createBlock(_component_v_checkbox, {
                              key: 0,
                              modelValue: alignToBinCenter.value,
                              "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((alignToBinCenter).value = $event)),
                              label: "Center bins",
                              density: "compact",
                              disabled: !disableBinCenterCheckbox.value,
                              "hide-details": "",
                              class: "mb-3"
                            }, null, 8, ["modelValue", "disabled"]))
                          : _createCommentVNode("", true)
                      ]),
                      (_unref(debugMode))
                        ? (_openBlock(), _createBlock(_component_v_card, {
                            key: 1,
                            height: "fit-content",
                            variant: "tonal",
                            class: "pa-2 mb-3"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_card_subtitle, { class: "pa-0" }, {
                                default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                                  _createTextVNode("Preview", -1)
                                ]))]),
                                _: 1
                              }),
                              _createElementVNode("div", _hoisted_8, [
                                _createElementVNode("div", null, "Original points: " + _toDisplayString(originalDataPointCount.value), 1),
                                _createElementVNode("div", null, "Aggregated points: " + _toDisplayString(foldedDataPointCount.value), 1),
                                _createElementVNode("div", null, "Time Bin: " + _toDisplayString(selectedTimeBin.value), 1),
                                _createElementVNode("div", null, "Folding Period: " + _toDisplayString(selectedFoldingPeriod.value), 1),
                                _createElementVNode("div", null, "Fold Type: " + _toDisplayString(selectedFoldType.value), 1),
                                _createElementVNode("div", null, "First bin: " + _toDisplayString(foldedData.value && foldedData.value.bins ? Object.keys(foldedData.value.bins)[0] : 'N/A'), 1),
                                (foldedData.value && foldedData.value.bins && foldedData.value.bins[Object.keys(foldedData.value.bins)[0]] && foldedData.value.values[Object.keys(foldedData.value.bins)[0]] && foldedData.value.values[Object.keys(foldedData.value.bins)[0]].date)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, " First bin time: "))
                                  : _createCommentVNode("", true)
                              ])
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", _hoisted_10, [
                        _createVNode(_component_v_btn, {
                          color: "primary",
                          onClick: saveFolding,
                          disabled: !canSave.value,
                          size: "small"
                        }, {
                          default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                            _createTextVNode(" Save Folding ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["disabled"]),
                        _createVNode(_component_v_btn, {
                          color: "secondary",
                          variant: "outlined",
                          onClick: closeDialog,
                          size: "small"
                        }, {
                          default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                            _createTextVNode(" Cancel ", -1)
                          ]))]),
                          _: 1
                        })
                      ])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, {
                cols: "12",
                md: "8"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card, {
                    variant: "outlined",
                    class: "pa-3",
                    style: {"height":"auto"}
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_card_title, null, {
                        default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                          _createTextVNode(" Time Series Comparison ", -1)
                        ]))]),
                        _: 1
                      }),
                      _createElementVNode("div", _hoisted_11, [
                        _createVNode(FoldedPlotlyGraph, {
                          datasets: graphData.value,
                          "show-errors": showErrors.value,
                          "fold-type": selectedFoldType.value,
                          colors: [theColor.value, '#333'],
                          timezones: selectedTimezone.value,
                          "data-options": [
                    {mode: 'markers'}, // options for the original data
                    {mode: 'markers'} // options for the folded data
                    ],
                          "error-bar-styles": [
                    {'thickness': 1, 'width': 0}, // original data error bar style
                    { 'thickness': 3, 'width': 0 } // folded data error bar style
                  ],
                          "config-options": {responsive: true, modeBarButtonsToRemove: ['sendDataToCloud','lasso2d', 'resetScale2d', ]},
                          onClick: handlePointClick,
                          "layout-options": {legend: {y:1.15, orientation:'h',bordercolor: '#ccc', borderwidth:1}}
                        }, null, 8, ["datasets", "show-errors", "fold-type", "colors", "timezones"])
                      ]),
                      _createElementVNode("div", _hoisted_12, [
                        (aggregationWarning.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(aggregationWarning.value), 1))
                          : _createCommentVNode("", true),
                        _cache[26] || (_cache[26] = _createTextVNode(" If you don't see any data, please press the \"Autoscale\" ", -1)),
                        _createVNode(_component_v_icon, {
                          size: "1.2em",
                          style: {"margin-top":"-0.1em"}
                        }, {
                          default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                            _createTextVNode("mdi-arrow-expand-all", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[27] || (_cache[27] = _createTextVNode(" button on the graph menu (visible when you hover over the graph), or try clicking the legend items to show/hide overlapping data. ", -1))
                      ])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})